import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  ACCESS,
  ACCESS_LABEL,
  EMP_LEVELS,
  MAX_ACTIVE_FINANCE_HOLDERS,
  initials,
  money,
  wouldCreateCycle,
  type AccessLevel,
  type Directory,
  type EmploymentLevel,
  type User,
} from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { useAuth, useCurrentUser } from '@/auth/AuthContext';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/components/Toast';
import { ConfirmDialog } from './ConfirmDialog';
import { DeactivateModal } from './DeactivateModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import {
  ASSIGNABLE_ACCESS,
  departmentsOf,
  financeSeatsTaken,
  managerName,
  splitName,
} from './helpers';

export interface EmployeeEditModalProps {
  target: User;
  users: readonly User[];
  directory: Directory;
  onClose: () => void;
  onChanged: () => Promise<void>;
}

/**
 * The one place an employee record is edited, opened from both the organization
 * chart and access management.
 *
 * Company email is shown but not editable: the server's `updateUser`
 * accepts name, department, position and employment level only, so offering the
 * other two would silently discard what the admin typed.
 */
export function EmployeeEditModal({
  target,
  users,
  directory,
  onClose,
  onChanged,
}: EmployeeEditModalProps): ReactNode {
  const { toast } = useToast();
  const { refreshIfSelf } = useAuth();
  const me = useCurrentUser();
  const initial = splitName(target.name);

  const [first, setFirst] = useState(initial.first);
  const [last, setLast] = useState(initial.last);
  const [department, setDepartment] = useState(target.department);
  const [position, setPosition] = useState(target.position);
  const [empId, setEmpId] = useState(target.empId);
  const [employmentLevel, setEmploymentLevel] = useState<EmploymentLevel>(target.employmentLevel);
  const [reportsTo, setReportsTo] = useState<string>(target.reportsTo ?? '');
  const [systemAccess, setSystemAccess] = useState<AccessLevel>(target.systemAccess);

  const [errors, setErrors] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const isMainAccessHolder = target.systemAccess === ACCESS.MAIN_ACCESS;
  const departments = useMemo(() => departmentsOf(users), [users]);
  const managerOptions = useMemo(
    () => users.filter((u) => u.active && u.id !== target.id),
    [users, target.id],
  );
  const seatsTaken = financeSeatsTaken(users, target.id);
  const financeSeatsFull = seatsTaken >= MAX_ACTIVE_FINANCE_HOLDERS;

  const name = `${first.trim()} ${last.trim()}`.trim();
  const nextReportsTo = reportsTo || null;

  const fieldsChanged =
    name !== target.name ||
    department.trim() !== target.department ||
    position.trim() !== target.position ||
    empId.trim() !== target.empId ||
    employmentLevel !== target.employmentLevel;
  const reportsToChanged = nextReportsTo !== (target.reportsTo ?? null);
  const accessChanged = systemAccess !== target.systemAccess;
  const anythingChanged = fieldsChanged || reportsToChanged || accessChanged;

  const validate = useCallback((): string[] => {
    const found: string[] = [];
    if (!first.trim() || !last.trim()) found.push('First and last name are required.');
    if (!department.trim()) found.push('Department is required.');
    if (!empId.trim()) found.push('Employee ID is required.');
    if (!position.trim()) found.push('Position is required.');
    if (reportsToChanged && wouldCreateCycle(directory, target.id, nextReportsTo)) {
      found.push(
        'That manager already reports to this employee — the change would create a reporting loop.',
      );
    }
    if (
      accessChanged &&
      systemAccess === ACCESS.FINANCE_REVIEW &&
      financeSeatsFull
    ) {
      found.push(
        `Maximum of ${MAX_ACTIVE_FINANCE_HOLDERS} active Finance holders already assigned. Remove one first.`,
      );
    }
    return found;
  }, [
    accessChanged,
    department,
    directory,
    financeSeatsFull,
    first,
    last,
    nextReportsTo,
    position,
    reportsToChanged,
    systemAccess,
    target.id,
  ]);

  const requestSave = useCallback((): void => {
    const found = validate();
    setErrors(found);
    if (found.length > 0) return;
    if (!anythingChanged) {
      toast('Nothing to save.', 'info');
      return;
    }
    setConfirming(true);
  }, [anythingChanged, toast, validate]);

  /**
   * Applied as separate calls because the server audits them separately. If a
   * later call is refused the earlier ones stand, so the directory is reloaded
   * before the error is re-thrown and the admin sees the real current state.
   */
  const applyChanges = useCallback(async (): Promise<void> => {
    try {
      if (fieldsChanged) {
        await api.admin.updateUser<User>({
          userId: target.id,
          name,
          department: department.trim(),
          position: position.trim(),
          employmentLevel,
          empId: empId.trim(),
        });
      }
      if (reportsToChanged) {
        await api.admin.setReportsTo<User>({ userId: target.id, managerId: nextReportsTo });
      }
      if (accessChanged) {
        await api.admin.setSystemAccess<User>({ userId: target.id, systemAccess });
      }
    } catch (err) {
      await onChanged();
      throw err;
    }
    await onChanged();
    // Editing yourself must update the session too, or the change shows in the
    // org chart while your profile and every new claim keep the old details.
    await refreshIfSelf(target.id);
    toast(`${name} updated.`, 'ok');
    setConfirming(false);
    onClose();
  }, [
    accessChanged,
    department,
    empId,
    employmentLevel,
    fieldsChanged,
    name,
    nextReportsTo,
    refreshIfSelf,
    onChanged,
    onClose,
    position,
    reportsToChanged,
    systemAccess,
    target.id,
    toast,
  ]);

  const reactivate = useCallback(async (): Promise<void> => {
    setBusy(true);
    setErrors([]);
    try {
      await api.admin.reactivateUser<User>({ userId: target.id });
      await onChanged();
      toast(`${target.name} reactivated.`, 'ok');
      onClose();
    } catch (err) {
      setErrors([errorMessage(err)]);
      setBusy(false);
    }
  }, [onChanged, onClose, target.id, target.name, toast]);

  return (
    <>
      <Modal title="Edit Employee" onClose={onClose} wide>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div className="tree-card" style={{ cursor: 'default', maxWidth: 'none' }}>
            <div className="av" aria-hidden="true">
              {initials(target.name)}
            </div>
            <div>
              <div className="nm">{target.name}</div>
              <div className="ps">{target.email}</div>
            </div>
          </div>
          <span className={target.active ? 'badge badge-approved' : 'badge badge-draft'}>
            {target.active ? 'Active' : 'Inactive'}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {/*
              Offered for every active employee, Main Access holders included —
              administrators lock themselves out too, and with several of them
              the point is that they can recover each other. Not offered for
              yourself: the server refuses it, and Change Password is the screen
              that exists for that.
            */}
            {target.active && target.id !== me.id ? (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setResettingPassword(true)}
              >
                Reset password
              </button>
            ) : null}
            {isMainAccessHolder ? null : target.active ? (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => setDeactivating(true)}
              >
                Deactivate
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-good btn-sm"
                onClick={() => void reactivate()}
                disabled={busy}
              >
                {busy ? <Spinner /> : null}
                Reactivate
              </button>
            )}
          </span>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="ee-first">First Name</label>
            <input id="ee-first" value={first} onChange={(e) => setFirst(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="ee-last">Last Name</label>
            <input id="ee-last" value={last} onChange={(e) => setLast(e.target.value)} />
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="ee-empid">Employee ID</label>
            <input id="ee-empid" value={empId} onChange={(e) => setEmpId(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="ee-email">Company Email</label>
            <input id="ee-email" value={target.email} readOnly disabled />
            <p className="field-note">
              The company email identifies the account and cannot be changed here. Employee IDs
              are unique — saving one that belongs to somebody else is refused.
            </p>
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="ee-dept">Department</label>
            <input
              id="ee-dept"
              list="ee-dept-list"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            />
            <datalist id="ee-dept-list">
              {departments.map((d) => (
                <option key={d} value={d} />
              ))}
            </datalist>
          </div>
          <div className="field">
            <label htmlFor="ee-position">Position</label>
            <input
              id="ee-position"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            />
          </div>
        </div>

        {/* Finance's two fields, shown because an administrator looking at an
            employee record should see everything on it, and read-only because
            Finance asked for fields a directory administrator cannot change.
            They are set on the Allowances & SAP codes screen. */}
        <div className="form-row">
          <div className="field">
            <label>Monthly allowance</label>
            <input
              value={target.monthlyAllowance == null ? 'Not set' : money(target.monthlyAllowance)}
              disabled
            />
            <p className="field-note">Set by Finance. Shown at Finance Review; never blocks a claim.</p>
          </div>
          <div className="field">
            <label>SAP vendor code</label>
            <input value={target.sapVendorCode || 'Not set'} disabled />
            <p className="field-note">Set by Finance. Appears on the Finance export.</p>
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="ee-level">Employment Level / Rank</label>
            <select
              id="ee-level"
              value={employmentLevel}
              onChange={(e) => setEmploymentLevel(e.target.value as EmploymentLevel)}
            >
              {EMP_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
            <p className="field-note">
              Executive rank sits at the top of the organization chart and skips Manager/Lead
              approval — those claims route straight to Finance Review.
            </p>
          </div>
          <div className="field">
            <label htmlFor="ee-reports-to">Reports To (change manager)</label>
            <select
              id="ee-reports-to"
              value={reportsTo}
              onChange={(e) => setReportsTo(e.target.value)}
            >
              <option value="">— (top of chart)</option>
              {managerOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} — {u.department}
                </option>
              ))}
            </select>
            <p className="field-note">
              Current: {managerName(users, target.reportsTo)}. This is the same change the chart
              makes when a card is dragged onto a new manager.
            </p>
          </div>
        </div>

        <div className="field">
          <label htmlFor="ee-access">System Access</label>
          <select
            id="ee-access"
            value={systemAccess}
            disabled={isMainAccessHolder}
            onChange={(e) => setSystemAccess(e.target.value as AccessLevel)}
          >
            {isMainAccessHolder ? (
              <option value={ACCESS.MAIN_ACCESS}>{ACCESS_LABEL[ACCESS.MAIN_ACCESS]}</option>
            ) : null}
            {ASSIGNABLE_ACCESS.map((a) => (
              <option
                key={a}
                value={a}
                disabled={
                  a === ACCESS.FINANCE_REVIEW &&
                  financeSeatsFull &&
                  target.systemAccess !== ACCESS.FINANCE_REVIEW
                }
              >
                {ACCESS_LABEL[a]}
                {a === ACCESS.FINANCE_REVIEW
                  ? ` (${seatsTaken} of ${MAX_ACTIVE_FINANCE_HOLDERS} seats taken)`
                  : ''}
              </option>
            ))}
          </select>
          <p className="field-note">
            {isMainAccessHolder
              ? 'Main Access is never assigned directly — use "Transfer Main Access" in Access Management.'
              : 'Being in the Finance department does not grant Finance access; it must be assigned here explicitly. A change is refused while this person still owns claims in progress.'}
          </p>
        </div>

        {errors.length > 0 ? (
          <div className="error-text">
            {errors.map((e) => (
              <div key={e}>• {e}</div>
            ))}
          </div>
        ) : null}

        <div className="cf-submitbar">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={requestSave}>
            Save Changes
          </button>
        </div>
      </Modal>

      {confirming ? (
        <ConfirmDialog
          title="Confirm Changes"
          confirmLabel="Confirm & Save"
          onCancel={() => setConfirming(false)}
          onConfirm={applyChanges}
        >
          {fieldsChanged ? (
            <p>
              Update <b>{target.name}</b>&apos;s profile details.
            </p>
          ) : null}
          {reportsToChanged ? (
            <p>
              Change reporting line from <b>{managerName(users, target.reportsTo)}</b> to{' '}
              <b>{managerName(users, nextReportsTo)}</b>. This affects future approval routing.
            </p>
          ) : null}
          {accessChanged ? (
            <p>
              Change system access from <b>{ACCESS_LABEL[target.systemAccess]}</b> to{' '}
              <b>{ACCESS_LABEL[systemAccess]}</b>.
            </p>
          ) : null}
        </ConfirmDialog>
      ) : null}

      {resettingPassword ? (
        <ResetPasswordModal target={target} onClose={() => setResettingPassword(false)} />
      ) : null}

      {deactivating ? (
        <DeactivateModal
          target={target}
          directory={directory}
          onClose={() => setDeactivating(false)}
          onChanged={async () => {
            await onChanged();
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
